import React, { Component } from "react";
import { Row, Col, Card, Space, Typography, PageHeader, Alert, Modal, Tag, Divider, Select, Tooltip } from "antd";
import { ColorPicker } from "antd5";
import {
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Form,
  FormItem,
  InputItem,
  InputNumberItem,
  ButtonItem,
  SwitchItem,
  SelectItem,
  TextAreaItem,
} from "../../../../Components/Items";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import axios from "axios";
import { connect } from "react-redux";

const { Title, Text, Paragraph } = Typography;

const FULL = { labelCol: { span: 24 }, wrapperCol: { span: 24 } };
const SIN_LABEL = { labelCol: { span: 0 }, wrapperCol: { span: 24 } };

const TIPOS_PREGUNTA = [
  { value: "escala", label: "Escala (opciones con puntos, ej. Malo=1 … Excelente=4)" },
  { value: "num", label: "Numérico (un valor contra un máximo)" },
  { value: "bool", label: "Sí / No" },
  { value: "texto", label: "Texto libre (no puntúa, solo queda como comentario)" },
];

const TIPO_ABREV = { escala: "Escala", num: "Numérico", bool: "Sí/No", texto: "Texto" };

const seccionVacia = () => ({
  nombre: "",
  peso: 0,
  preguntas: [],
});

// Solo "desde"; el "hasta" se calcula solo.
const clasificacionesPorDefecto = () => [
  { nombre: "Aprobado", desde: 90, color: "#52c41a" },
  { nombre: "Condicionado", desde: 70, color: "#faad14" },
  { nombre: "Rechazado", desde: 0, color: "#f5222d" },
];

const preguntaVacia = (escalaGeneral) => ({
  descripcion: "",
  textoAyuda: "",
  tipo: "escala",
  peso: 0,
  permiteNoAplica: true,
  obligatoria: true,
  cuentaParaResultado: true,
  opciones:
    escalaGeneral && escalaGeneral.length > 0
      ? JSON.parse(JSON.stringify(escalaGeneral))
      : [
          { etiqueta: "Cumple", valor: 10 },
          { etiqueta: "No cumple", valor: 0 },
        ],
});

const maximoDePregunta = (p) => {
  if (!p || p.tipo === "texto" || p.cuentaParaResultado === false) return 0;
  if (p.tipo === "escala") return Math.max(0, ...(p.opciones || []).map((o) => Number(o.valor) || 0));
  if (p.tipo === "num") return Number(p.valorMaximo) || 0;
  if (p.tipo === "bool") return p.valorSiCumple ?? 100;
  return 0;
};

const techoClasificaciones = (secciones, modoPuntuacion) => {
  if (modoPuntuacion !== "puntos") return 100;
  return (secciones || []).reduce(
    (acc, s) => acc + (s.preguntas || []).reduce((acc2, p) => acc2 + maximoDePregunta(p), 0),
    0,
  );
};

const calcularRangosPreview = (clasificaciones, techo) => {
  const conIndice = (clasificaciones || []).map((c, i) => ({ ...c, __i: i }));
  const validas = conIndice.filter((c) => c?.nombre && c.desde !== undefined && c.desde !== null);
  if (validas.length === 0) return [];
  const ordenadas = [...validas].sort((a, b) => Number(b.desde) - Number(a.desde));
  return ordenadas.map((c, i) => ({
    ...c,
    desde: i === ordenadas.length - 1 ? 0 : Number(c.desde),
    hasta: i === 0 ? techo : Number(ordenadas[i - 1].desde) - 1,
  }));
};

class VistaPlantillaEvaluacion extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    plantilla: null,
    linaje: [],
    secciones: [],
    clasificaciones: [],
    modoPuntuacion: "porcentaje",
    tiposDeProveedor: [],
    escalaGeneral: [],
    numeroGeneral: {},
    booleanoGeneral: {},
  };

  componentDidMount() {
    const id = this.props.match.params.id;
    this.cargarTiposDeProveedor();
    if (id === "nuevo") {
      const secciones = [seccionVacia()];
      const clasificaciones = clasificacionesPorDefecto();
      const modoPuntuacion = "porcentaje";
      this.setState(
        {
          plantilla: null,
          linaje: [],
          secciones,
          clasificaciones,
          modoPuntuacion,
          escalaGeneral: [],
          numeroGeneral: {},
          booleanoGeneral: {},
        },
        () => {
          this.formRef.current?.setFieldsValue({ secciones, clasificaciones, modoPuntuacion });
        },
      );
    } else {
      this.cargarDatos(id);
    }
  }

  cargarTiposDeProveedor = () => {
    axios
      .post(
        "tiposdeproveedores/selectTodosTiposDeProveedores",
        { empresaId: this.props.empresaId, valor: 1 },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (Array.isArray(res.data)) {
          this.setState({ tiposDeProveedor: res.data });
        }
      })
      .catch(() => {});
  };

  cargarDatos = (id) => {
    this.setState({ loading: true });
    axios
      .post("plantillasEvaluacion/datosPlantilla", { id }, { headers: this.props.headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          this.setState(
            {
              plantilla: res.data.plantilla,
              linaje: res.data.linaje || [],
              secciones: res.data.plantilla.secciones || [],
              clasificaciones: res.data.plantilla.clasificaciones || [],
              modoPuntuacion: res.data.plantilla.modoPuntuacion || "porcentaje",
              escalaGeneral: res.data.plantilla.escalaGeneral || [],
              numeroGeneral: res.data.plantilla.numeroGeneral || {},
              booleanoGeneral: res.data.plantilla.booleanoGeneral || {},
            },
            () => this.formRef.current?.setFieldsValue(res.data.plantilla),
          );
        } else {
          Modal.error({ title: "Atención", content: res.data.message });
        }
      })
      .catch(() => {
        Modal.error({ title: "Error", content: "Ocurrió un error al cargar la plantilla." });
      })
      .finally(() => this.setState({ loading: false }));
  };

  atras = () => {
    this.props.history.goBack();
  };

  guardarPlantilla = async () => {
    try {
      const values = await this.formRef.current.validateFields();

      const errorPesos = this.errorDePesos(values.secciones || [], values.modoPuntuacion);
      if (errorPesos) {
        return Modal.warning({ title: "Revisa los pesos", content: errorPesos });
      }

      const techo = techoClasificaciones(values.secciones, values.modoPuntuacion);
      const errorClasificaciones = this.errorDeClasificaciones(values.clasificaciones || [], techo);
      if (errorClasificaciones) {
        return Modal.warning({ title: "Revisa las clasificaciones", content: errorClasificaciones });
      }

      this.setState({ loading: true });

      const secciones = (values.secciones || []).map((seccion, index) => ({
        ...seccion,
        orden: index,
        preguntas: (seccion.preguntas || []).map((pregunta, pIndex) => ({
          ...pregunta,
          orden: pIndex,
        })),
      }));

      const payload = {
        ...values,
        secciones,
        _id: this.state.plantilla?._id,
        empresaId: this.props.empresaId,
      };

      axios
        .post("plantillasEvaluacion/guardarPlantilla", payload, {
          headers: this.props.headersToken,
        })
        .then((res) => {
          if (res.data.success === 2) {
            Modal.success({ title: "Éxito", content: res.data.message });
            if (!this.state.plantilla?._id) {
              this.props.history.replace(
                `/panel/evaluacionProveedoresConfiguracion/plantilla/${res.data.plantilla._id}`,
              );
            }
            this.cargarDatos(res.data.plantilla._id);
          } else {
            Modal.error({ title: "Error", content: res.data.message });
          }
        })
        .catch(() => {
          Modal.error({ title: "Error", content: "Error de red al guardar." });
        })
        .finally(() => this.setState({ loading: false }));
    } catch (errorInfo) {
      console.error("Faltan campos obligatorios:", errorInfo);
      Modal.warning({
        title: "Faltan datos",
        content: "Completa los campos obligatorios marcados en rojo antes de guardar.",
      });
    }
  };

  autorizarPlantilla = () => {
    const { plantilla } = this.state;
    if (!plantilla?._id) return;

    Modal.confirm({
      title: "¿Autorizar esta plantilla?",
      content:
        "Una vez autorizada, la plantilla queda inmutable. Para cambiarla en el futuro deberás clonarla a una nueva versión.",
      okText: "Sí, autorizar",
      cancelText: "Cancelar",
      onOk: () => {
        this.setState({ loading: true });
        axios
          .post(
            "plantillasEvaluacion/autorizarPlantilla",
            { _id: plantilla._id },
            { headers: this.props.headersToken },
          )
          .then((res) => {
            if (res.data.success === 2) {
              Modal.success({ title: "Éxito", content: res.data.message });
              this.cargarDatos(plantilla._id);
            } else {
              Modal.error({ title: "Error", content: res.data.message });
            }
          })
          .catch(() => Modal.error({ title: "Error", content: "Error al autorizar." }))
          .finally(() => this.setState({ loading: false }));
      },
    });
  };

  clonarPlantilla = () => {
    const { plantilla } = this.state;
    if (!plantilla?._id) return;

    this.setState({ loading: true });
    axios
      .post(
        "plantillasEvaluacion/clonarPlantilla",
        { _id: plantilla._id },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          Modal.success({ title: "Éxito", content: res.data.message });
          this.props.history.push(
            `/panel/evaluacionProveedoresConfiguracion/plantilla/${res.data.plantilla._id}`,
          );
        } else {
          Modal.error({ title: "Error", content: res.data.message });
        }
      })
      .catch(() => Modal.error({ title: "Error", content: "Error al clonar la plantilla." }))
      .finally(() => this.setState({ loading: false }));
  };

  errorDePesos = (secciones, modoPuntuacion = "porcentaje") => {
    if (!secciones || secciones.length === 0) {
      return "Agrega al menos una sección.";
    }
    if (modoPuntuacion === "puntos") {
      const tieneComputable = secciones.some((s) =>
        (s.preguntas || []).some((p) => p.tipo !== "texto" && p.cuentaParaResultado !== false),
      );
      return tieneComputable
        ? null
        : "La plantilla necesita al menos una pregunta que puntúe (no solo de texto o marcadas como 'no cuenta').";
    }
    // Pesos relativos entre sí; no necesitan sumar 100, solo que no estén todos en 0.
    const sumaSecciones = secciones.reduce((acc, s) => acc + (Number(s.peso) || 0), 0);
    if (sumaSecciones <= 0) {
      return "Reparte algo de peso entre las secciones (no pueden estar todas en 0).";
    }
    for (const seccion of secciones) {
      const ponderables = (seccion.preguntas || []).filter(
        (p) => p.tipo !== "texto" && p.cuentaParaResultado !== false,
      );
      if (ponderables.length === 0) continue;
      const sumaPreguntas = ponderables.reduce((acc, p) => acc + (Number(p.peso) || 0), 0);
      if (sumaPreguntas <= 0) {
        return `Reparte algo de peso entre las preguntas de "${seccion.nombre || "sección sin nombre"}" (no pueden estar todas en 0).`;
      }
    }
    return null;
  };

  // Solo se valida el "desde"; el "hasta" lo calcula el sistema, así que nunca hay huecos ni traslapes.
  errorDeClasificaciones = (clasificaciones, techo) => {
    if (!Array.isArray(clasificaciones) || clasificaciones.length === 0) {
      return "Agrega al menos una clasificación (ej. Aprobado, Rechazado).";
    }
    const desdeVistos = new Set();
    for (const c of clasificaciones) {
      const desde = Number(c.desde);
      if (!c.nombre) return "Todas las clasificaciones necesitan un nombre.";
      if (Number.isNaN(desde) || desde < 0 || desde > techo) {
        return `El valor "desde" de "${c.nombre}" debe estar entre 0 y ${techo}.`;
      }
      if (desdeVistos.has(desde)) {
        return `Dos clasificaciones no pueden empezar en el mismo número (${desde}) — cambia el "desde" de "${c.nombre}".`;
      }
      desdeVistos.add(desde);
    }
    return null;
  };

  // Aplica varios cambios a la vez a una pregunta, leyendo el árbol de secciones fresco del Form
  aplicarCambiosPregunta = (seccionIndex, preguntaIndex, cambios) => {
    const valoresActuales = this.formRef.current?.getFieldsValue();
    const secciones = JSON.parse(
      JSON.stringify(valoresActuales?.secciones || this.state.secciones || []),
    );
    const pregunta = secciones[seccionIndex]?.preguntas?.[preguntaIndex];
    if (!pregunta) return;

    Object.assign(pregunta, cambios);
    this.formRef.current?.setFieldsValue({ secciones });
    this.setState({ secciones });
  };

  copiarOpcionesAnteriores = (seccionIndex, preguntaIndex) => {
    const valoresActuales = this.formRef.current?.getFieldsValue();
    const anterior = valoresActuales?.secciones?.[seccionIndex]?.preguntas?.[preguntaIndex - 1];
    if (!anterior || anterior.tipo !== "escala" || !anterior.opciones?.length) return;

    this.aplicarCambiosPregunta(seccionIndex, preguntaIndex, {
      opciones: JSON.parse(JSON.stringify(anterior.opciones)),
    });
  };

  usarEscalaGeneral = (seccionIndex, preguntaIndex) => {
    const { escalaGeneral } = this.state;
    if (!escalaGeneral || escalaGeneral.length === 0) return;

    this.aplicarCambiosPregunta(seccionIndex, preguntaIndex, {
      opciones: JSON.parse(JSON.stringify(escalaGeneral)),
    });
  };

  usarNumeroGeneral = (seccionIndex, preguntaIndex) => {
    const { numeroGeneral } = this.state;
    if (
      !numeroGeneral ||
      (numeroGeneral.valorMinimo == null &&
        numeroGeneral.valorMaximo == null &&
        !numeroGeneral.etiquetaMinimo &&
        !numeroGeneral.etiquetaMaximo)
    )
      return;

    const cambios = {};
    if (numeroGeneral.valorMinimo != null) cambios.valorMinimo = numeroGeneral.valorMinimo;
    if (numeroGeneral.valorMaximo != null) cambios.valorMaximo = numeroGeneral.valorMaximo;
    if (numeroGeneral.etiquetaMinimo) cambios.etiquetaMinimo = numeroGeneral.etiquetaMinimo;
    if (numeroGeneral.etiquetaMaximo) cambios.etiquetaMaximo = numeroGeneral.etiquetaMaximo;
    this.aplicarCambiosPregunta(seccionIndex, preguntaIndex, cambios);
  };

  usarBooleanoGeneral = (seccionIndex, preguntaIndex) => {
    const { booleanoGeneral } = this.state;
    if (!booleanoGeneral || booleanoGeneral.valorSiCumple == null) return;

    this.aplicarCambiosPregunta(seccionIndex, preguntaIndex, { valorSiCumple: booleanoGeneral.valorSiCumple });
  };

  normColor = (color) => {
    if (!color) return null;
    if (typeof color === "string") return color;
    if (typeof color.toHexString === "function") return color.toHexString();
    return null;
  };

  sumaPesoIndicador = (peso, etiqueta = "Suma de pesos (relativa)") => {
    const total = Math.round(peso);
    return (
      <Tag color={total > 0 ? "blue" : "red"} style={{ fontSize: 13 }}>
        {etiqueta}: {total}
      </Tag>
    );
  };

  totalPuntosPosibles = () => {
    const total = (this.state.secciones || []).reduce(
      (acc, s) => acc + (s.preguntas || []).reduce((acc2, p) => acc2 + maximoDePregunta(p), 0),
      0,
    );
    return (
      <Tag color="blue" style={{ fontSize: 13 }}>
        Total de puntos posibles: {total}
      </Tag>
    );
  };

  render() {
    const { plantilla, secciones, linaje, modoPuntuacion, tiposDeProveedor } = this.state;
    const disabled = !!plantilla?.autorizado;
    const esNueva = !plantilla?._id;
    const esPuntos = modoPuntuacion === "puntos";

    const sumaSecciones = (secciones || []).reduce((acc, s) => acc + (Number(s.peso) || 0), 0);

    const techo = techoClasificaciones(secciones, modoPuntuacion);
    const clasificacionesPreview = calcularRangosPreview(this.state.clasificaciones, techo);
    const hastaPorIndice = {};
    clasificacionesPreview.forEach((c) => {
      hastaPorIndice[c.__i] = c.hasta;
    });

    return (
      <div style={{ padding: "0 20px" }}>
        <PageHeader
          title={
            esNueva
              ? "Nueva Plantilla de Evaluación"
              : `Plantilla: ${plantilla.nombre} (v${plantilla.versionNumero}${plantilla.versionEtiqueta ? ` - ${plantilla.versionEtiqueta}` : ""})`
          }
          subTitle="Define las secciones y preguntas ponderadas de la evaluación."
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 15,
          }}
          tags={
            !esNueva &&
            (plantilla.autorizado ? (
              <Tag color="green">Autorizada</Tag>
            ) : (
              <Tag color="orange">Borrador</Tag>
            ))
          }
          extra={[
            !disabled && (
              <ButtonItem
                key="btn-guardar"
                verde
                onClick={this.guardarPlantilla}
                size="large"
                loading={this.state.loading}
                className="botonAgregar"
                icon={<SaveOutlined />}
              >
                Guardar Plantilla
              </ButtonItem>
            ),
            !disabled && !esNueva && this.props.permisoAutorizarPlantilla && (
              <ButtonItem
                key="btn-autorizar"
                azul
                onClick={this.autorizarPlantilla}
                size="large"
                loading={this.state.loading}
                className="botonAgregar"
                icon={<CheckCircleOutlined />}
              >
                Autorizar
              </ButtonItem>
            ),
            !esNueva && (
              <ButtonItem
                key="btn-clonar"
                naranja
                onClick={this.clonarPlantilla}
                size="large"
                loading={this.state.loading}
                className="botonAgregar"
                icon={<CopyOutlined />}
              >
                Clonar Nueva Versión
              </ButtonItem>
            ),
          ]}
          onBack={this.atras}
        />

        {disabled && (
          <Alert
            style={{ marginBottom: 15 }}
            type="warning"
            showIcon
            message="Esta plantilla está autorizada y no puede ser modificada directamente."
            description="Usa 'Clonar Nueva Versión' para crear una copia editable."
          />
        )}

        {linaje.length > 1 && (
          <Alert
            style={{ marginBottom: 15 }}
            type="info"
            showIcon
            message="Historial de versiones de esta plantilla"
            description={
              <>
                <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
                  Cada vez que clonas esta plantilla se crea una nueva versión. Haz clic en
                  cualquiera para abrir esa versión y revisar exactamente qué secciones,
                  preguntas y pesos tenía.
                </Paragraph>
                <Space wrap>
                  {linaje.map((v) => {
                    const esActual = v._id === plantilla?._id;
                    return (
                      <Tooltip
                        key={v._id}
                        title={esActual ? "Estás viendo esta versión" : "Ver esta versión y sus cambios"}
                      >
                        <Tag
                          color={esActual ? "blue" : v.estado === 0 ? "default" : "green"}
                          style={{ cursor: esActual ? "default" : "pointer" }}
                          onClick={() =>
                            !esActual &&
                            this.props.history.push(
                              `/panel/evaluacionProveedoresConfiguracion/plantilla/${v._id}`,
                            )
                          }
                        >
                          v{v.versionNumero}
                          {v.versionEtiqueta ? ` - ${v.versionEtiqueta}` : ""}
                          {v.autorizado ? " ✓" : ""}
                          {v.estado === 0 ? " (Deshabilitada)" : ""}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </Space>
              </>
            }
          />
        )}

        <Alert
          style={{ marginBottom: 15 }}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="¿Cómo se arma esta plantilla?"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              Una plantilla se divide en <b>Secciones</b> (ej. "Calidad", "Entrega") y cada
              sección tiene <b>Preguntas</b>. En modo <b>Porcentaje</b>, cada sección/pregunta
              tiene un <b>peso relativo</b> (no necesita sumar 100 exacto — 60/30/10 calcula
              igual que 6/3/1) y el resultado es un promedio ponderado de 0 a 100. En modo{" "}
              <b>Puntos</b>, cada pregunta vale sus puntos tal cual y el resultado es la{" "}
              <b>suma</b> de esos puntos (ej. 150+150+150+150+400 = hasta 1000). Al final, el
              sistema clasifica ese número según las <b>clasificaciones</b> que definas abajo
              (tú eliges los nombres y los rangos, en la misma escala que uses).
            </Paragraph>
          }
        />

        <div
          style={{
            backgroundColor: "white",
            padding: 20,
            borderRadius: 8,
            border: "1px solid rgb(247, 247, 247)",
          }}
        >
          <Form
            ref={this.formRef}
            onValuesChange={(_, allValues) => {
              this.setState({
                secciones: allValues.secciones || [],
                clasificaciones: allValues.clasificaciones || [],
                modoPuntuacion: allValues.modoPuntuacion || "porcentaje",
                escalaGeneral: allValues.escalaGeneral || [],
                numeroGeneral: allValues.numeroGeneral || {},
                booleanoGeneral: allValues.booleanoGeneral || {},
              });
            }}
          >
            <DynamicCardForm title="General" collapsable defaultOpen>
              <Row gutter={[16, 16]}>
                <Col span={10}>
                  <FormItem
                    name="nombre"
                    label="Nombre de la Plantilla"
                    required
                    margin
                    {...FULL}
                    tooltip="Cómo se va a identificar esta plantilla en la lista. Ej. 'Evaluación IATF Proveedores' o 'Check List Proveedores de Servicios'."
                  >
                    <InputItem placeholder="Ej. Evaluación IATF Proveedores" disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={8}>
                  <FormItem
                    name="areaSector"
                    label="Área / Sector "
                    margin
                    {...FULL}
                    tooltip="Referencia interna, ej. 'Manufactura' o 'Logística'."
                  >
                    <InputItem placeholder="Ej. Manufactura" disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem
                    name="versionEtiqueta"
                    label="Etiqueta de Versión"
                    margin
                    {...FULL}
                    tooltip="Texto libre para reconocer esta versión en auditorías, ej. 'Rev. 03'. El número de versión (v1, v2...) se asigna solo."
                  >
                    <InputItem placeholder="Ej. Rev. 03" disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <FormItem
                    name="descripcion"
                    label="Descripción "
                    margin
                    {...FULL}
                    tooltip="Explica para qué sirve esta plantilla o a qué proveedores aplica."
                  >
                    <TextAreaItem rows={2} disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={10}>
                  <FormItem
                    name="tipoProveedorId"
                    label="Tipo de Proveedor"
                    margin
                    {...FULL}
                    tooltip="Relaciona esta plantilla con un Tipo de Proveedor de tu catálogo. Sirve para poder filtrarla/asignarla más fácil a proveedores de ese tipo. Déjalo vacío si aplica a cualquier tipo."
                  >
                    <SelectItem disabled={disabled} allowClear placeholder="Todos los tipos de proveedor">
                      {(tiposDeProveedor || []).map((t) => (
                        <Select.Option key={t._id} value={t._id}>
                          {t.nombre}
                          {t.descripcion ? ` - ${t.descripcion}` : ""}
                        </Select.Option>
                      ))}
                    </SelectItem>
                  </FormItem>
                </Col>
              </Row>
            </DynamicCardForm>

            <DynamicCardForm title="Modo de puntuación" collapsable defaultOpen>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <FormItem
                    name="modoPuntuacion"
                    label="¿Cómo se calcula la calificación?"
                    {...FULL}
                    tooltip="Porcentaje: pesos relativos, resultado siempre 0-100 (promedio ponderado). Puntos: cada pregunta vale sus puntos y el resultado es la suma directa (ej. formatos que suman hasta 1000)."
                  >
                    <SelectItem disabled={disabled} style={{ width: 320 }}>
                      <Select.Option value="porcentaje">Porcentaje (0-100, pesos relativos)</Select.Option>
                      <Select.Option value="puntos">Puntos (suma directa, sin límite fijo)</Select.Option>
                    </SelectItem>
                  </FormItem>
                </Col>
              </Row>
            </DynamicCardForm>

            <DynamicCardForm title="Clasificaciones" collapsable defaultOpen>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="¿Para qué son las clasificaciones?"
                description={
                  esPuntos ? (
                    <>
                      Solo pones el <b>"Desde"</b> de cada nivel (ej. "Aceptable" desde 700). La más baja siempre
                      empieza en 0 y la más alta llega hasta el total de puntos posibles de tu
                      plantilla ({techo}).
                    </>
                  ) : (
                    <>
                      Solo pones el <b>"Desde"</b> de cada nivel (ej. "Aceptable" desde 66, "Malo"
                      desde 0). La más baja
                      siempre empieza en 0 y la más alta llega hasta 100
                    </>
                  )
                }
              />
              <div style={{ marginBottom: 8 }}>
                {clasificacionesPreview.map((c) => (
                  <Tag key={c.__i} color={c.color || "#52c41a"} style={{ marginBottom: 4 }}>
                    {c.nombre}: {c.desde} - {c.hasta}
                  </Tag>
                ))}
              </div>

              <Form.List name="clasificaciones">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={[8, 8]} key={key} align="top">
                        <Col span={7}>
                          <FormItem
                            {...restField}
                            name={[name, "nombre"]}
                            label="Nombre"
                            required
                            {...FULL}
                            tooltip="Como le dicen en tu empresa a este nivel, ej. 'Aceptable', 'Deficiente', 'Aprobado'."
                          >
                            <InputItem placeholder="Ej. Aceptable" disabled={disabled} />
                          </FormItem>
                        </Col>
                        <Col span={5}>
                          <FormItem
                            {...restField}
                            name={[name, "desde"]}
                            label="Desde"
                            required
                            {...FULL}
                            tooltip={
                              esPuntos
                                ? "Puntos mínimos (inclusive) de este rango. El 'hasta' se calcula solo."
                                : "Calificación mínima (0-100, inclusive) de este rango. El 'hasta' se calcula solo."
                            }
                          >
                            <InputNumberItem
                              min={0}
                              max={techo}
                              style={{ width: "100%" }}
                              disabled={disabled}
                            />
                          </FormItem>
                        </Col>
                        <Col span={5}>
                          <FormItem label="Hasta" {...FULL} tooltip="Se calcula solo a partir del 'Desde' de todas las clasificaciones — no se puede editar.">
                            <Tag style={{ width: "100%", textAlign: "center", padding: "5px 0" }}>
                              {hastaPorIndice[name] ?? "?"}
                            </Tag>
                          </FormItem>
                        </Col>
                        <Col span={4}>
                          <FormItem
                            {...restField}
                            name={[name, "color"]}
                            label="Color"
                            {...FULL}
                            getValueFromEvent={this.normColor}
                            tooltip="Color del tag con el que se va a ver esta clasificación en las listas y reportes."
                          >
                            <ColorPicker disabledAlpha format="hex" size="middle" showText disabled={disabled} />
                          </FormItem>
                        </Col>
                        <Col span={2}>
                          {!disabled && (
                            <FormItem label={<>&nbsp;</>} {...FULL} style={{ marginBottom: 0 }}>
                              <ButtonItem
                                danger
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                tooltip="Eliminar esta clasificación"
                              />
                            </FormItem>
                          )}
                        </Col>
                      </Row>
                    ))}

                    {!disabled && (
                      <ButtonItem
                        type="dashed"
                        onClick={() => add({ nombre: "", desde: 0, color: "#1890ff" })}
                        block
                        icon={<PlusOutlined />}
                        style={{ marginBottom: 8 }}
                      >
                        Añadir Clasificación
                      </ButtonItem>
                    )}
                  </>
                )}
              </Form.List>
            </DynamicCardForm>

            <DynamicCardForm title="Datos que se piden al llenar" collapsable defaultOpen>
              <Paragraph type="secondary" style={{ marginTop: -8 }}>
                Activa estos interruptores si, además del proveedor (que siempre se pide), quieres
                capturar contexto adicional cada vez que alguien llene esta evaluación.
              </Paragraph>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <FormItem
                    name="requiereCliente"
                    label="Pedir Cliente"
                    valuePropName="checked"
                    {...FULL}
                    tooltip="Si se activa, al llenar la evaluación se pedirá elegir un cliente relacionado (ej. a qué cliente se le entregó el servicio del proveedor)."
                  >
                    <SwitchItem disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={8}>
                  <FormItem
                    name="requiereResponsable"
                    label="Pedir Responsable"
                    valuePropName="checked"
                    {...FULL}
                    tooltip="Si se activa, al llenar la evaluación se pedirá elegir quién es responsable de esta evaluación (ej. quién la recibió o quién da seguimiento)."
                  >
                    <SwitchItem disabled={disabled} />
                  </FormItem>
                </Col>
              </Row>
            </DynamicCardForm>

            <DynamicCardForm title="Configuración General de Preguntas" collapsable defaultOpen>
              <Paragraph type="secondary" style={{ marginTop: -8 }}>
                Si la mayoría de tus preguntas comparten la
                misma configuración, defínela aquí una sola vez y luego dale "Usar configuración
                general" en cualquier pregunta para aplicarla. Cada pregunta puede seguir
                personalizando la suya si necesita una distinta.
              </Paragraph>

              <Text strong style={{ fontSize: 13 }}>Escala General</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                Para preguntas tipo Escala, ej. Malo=1 … Excelente=4.
              </Paragraph>
              <Form.List name="escalaGeneral">
                {(fields, { add, remove }) => (
                  <div style={{ marginBottom: 8 }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={[8, 8]} key={key} align="middle">
                        <Col span={11}>
                          <FormItem
                            {...restField}
                            name={[name, "etiqueta"]}
                            {...SIN_LABEL}
                            tooltip="Texto visible de la opción, ej. 'Cumple' o 'Excelente'."
                          >
                            <InputItem placeholder="Etiqueta (ej. Excelente)" disabled={disabled} />
                          </FormItem>
                        </Col>
                        <Col span={9}>
                          <FormItem
                            {...restField}
                            name={[name, "valor"]}
                            {...SIN_LABEL}
                            tooltip="Puntos que otorga elegir esta opción."
                          >
                            <InputNumberItem placeholder="Valor / puntos" style={{ width: "100%" }} disabled={disabled} />
                          </FormItem>
                        </Col>
                        <Col span={4}>
                          {!disabled && (
                            <FormItem {...SIN_LABEL}>
                              <ButtonItem
                                danger
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                tooltip="Eliminar esta opción"
                              />
                            </FormItem>
                          )}
                        </Col>
                      </Row>
                    ))}
                    {!disabled && (
                      <ButtonItem
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => add({ etiqueta: "", valor: 0 })}
                      >
                        Agregar Opción a la Escala General
                      </ButtonItem>
                    )}
                  </div>
                )}
              </Form.List>

              <Text strong style={{ fontSize: 13 }}>Numérico General</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                Para preguntas tipo Numérico, ej. si la mayoría van de 0 a 10.
              </Paragraph>
              <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
                <Col span={6}>
                  <FormItem
                    name={["numeroGeneral", "valorMinimo"]}
                    label="Valor Mínimo General"
                    {...FULL}
                    tooltip="Valor mínimo por defecto para preguntas nuevas de tipo Numérico."
                  >
                    <InputNumberItem style={{ width: "100%" }} disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem
                    name={["numeroGeneral", "etiquetaMinimo"]}
                    label="Nombre del Mínimo (opcional)"
                    {...FULL}
                    tooltip="Cómo se le llama a ese extremo, ej. 'Malo' o '0 retrasos'. Solo informativo, no afecta el cálculo."
                  >
                    <InputItem placeholder="Ej. Malo" disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem
                    name={["numeroGeneral", "valorMaximo"]}
                    label="Valor Máximo General"
                    {...FULL}
                    tooltip="Valor máximo por defecto para preguntas nuevas de tipo Numérico."
                  >
                    <InputNumberItem style={{ width: "100%" }} disabled={disabled} />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem
                    name={["numeroGeneral", "etiquetaMaximo"]}
                    label="Nombre del Máximo (opcional)"
                    {...FULL}
                    tooltip="Cómo se le llama a ese extremo, ej. 'Excelente' o '10 entregas a tiempo'. Solo informativo, no afecta el cálculo."
                  >
                    <InputItem placeholder="Ej. Excelente" disabled={disabled} />
                  </FormItem>
                </Col>
              </Row>

              <Text strong style={{ fontSize: 13 }}>Sí/No General</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                Para preguntas tipo Sí/No, los puntos que otorga por defecto responder "Sí".
              </Paragraph>
              <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
                <Col span={6}>
                  <FormItem
                    name={["booleanoGeneral", "valorSiCumple"]}
                    label="Puntos si Cumple General"
                    {...FULL}
                    tooltip="Puntos por defecto para preguntas nuevas de tipo Sí/No cuando la respuesta es 'Sí'."
                  >
                    <InputNumberItem style={{ width: "100%" }} disabled={disabled} />
                  </FormItem>
                </Col>
              </Row>
            </DynamicCardForm>

            <Divider />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Secciones
              </Title>
              {esPuntos ? this.totalPuntosPosibles() : this.sumaPesoIndicador(sumaSecciones)}
            </div>

            <Form.List name="secciones">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <SeccionCard
                      key={key}
                      name={name}
                      restField={restField}
                      index={index}
                      disabled={disabled}
                      esPuntos={esPuntos}
                      onRemove={() => remove(name)}
                      seccionValores={secciones?.[index]}
                      escalaGeneral={this.state.escalaGeneral}
                      numeroGeneral={this.state.numeroGeneral}
                      booleanoGeneral={this.state.booleanoGeneral}
                      onCopiarOpciones={(preguntaIndex) => this.copiarOpcionesAnteriores(index, preguntaIndex)}
                      onUsarEscalaGeneral={(preguntaIndex) => this.usarEscalaGeneral(index, preguntaIndex)}
                      onUsarNumeroGeneral={(preguntaIndex) => this.usarNumeroGeneral(index, preguntaIndex)}
                      onUsarBooleanoGeneral={(preguntaIndex) => this.usarBooleanoGeneral(index, preguntaIndex)}
                    />
                  ))}

                  {!disabled && (
                    <ButtonItem
                      type="dashed"
                      onClick={() => add(seccionVacia())}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginBottom: 20 }}
                    >
                      Añadir Sección
                    </ButtonItem>
                  )}
                </>
              )}
            </Form.List>
          </Form>
        </div>
      </div>
    );
  }
}

function SeccionCard({
  name,
  restField,
  index,
  disabled,
  onRemove,
  seccionValores,
  esPuntos,
  onCopiarOpciones,
  escalaGeneral,
  onUsarEscalaGeneral,
  numeroGeneral,
  onUsarNumeroGeneral,
  booleanoGeneral,
  onUsarBooleanoGeneral,
}) {
  const preguntasSeccion = seccionValores?.preguntas || [];
  const sumaPreguntas = preguntasSeccion
    .filter((p) => p?.tipo !== "texto" && p?.cuentaParaResultado !== false)
    .reduce((acc, p) => acc + (Number(p?.peso) || 0), 0);
  const tienePonderables = preguntasSeccion.some(
    (p) => p?.tipo !== "texto" && p?.cuentaParaResultado !== false,
  );
  const totalPuntosSeccion = preguntasSeccion.reduce((acc, p) => acc + maximoDePregunta(p), 0);

  return (
    <Card
      size="small"
      style={{ marginBottom: 20, background: "#f4f5f7", border: "1px solid #dfe1e6" }}
      title={<span style={{ color: "#5e6c84", fontSize: 13 }}>Sección {index + 1}</span>}
      extra={
        !disabled && (
          <MinusCircleOutlined
            onClick={onRemove}
            style={{ color: "#ff4d4f", fontSize: 16, cursor: "pointer" }}
          />
        )
      }
    >
      <Row gutter={[12, 12]}>
        <Col span={esPuntos ? 24 : 16}>
          <FormItem
            {...restField}
            name={[name, "nombre"]}
            label="Nombre de la Sección"
            required
            margin
            {...FULL}
            tooltip="Ej. 'Calidad', 'Entrega', 'Atención del personal'. Agrupa las preguntas relacionadas."
          >
            <InputItem placeholder="Ej. Calidad" disabled={disabled} />
          </FormItem>
        </Col>
        {!esPuntos && (
          <Col span={8}>
            <FormItem
              {...restField}
              name={[name, "peso"]}
              label="Peso de la Sección"
              required
              margin
              {...FULL}
              tooltip="Peso RELATIVO de esta sección frente a las demás (no necesita sumar 100 con las otras secciones — 60/30/10 calcula igual que 6/3/1)."
            >
              <InputNumberItem min={0} style={{ width: "100%" }} disabled={disabled} />
            </FormItem>
          </Col>
        )}
      </Row>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, marginTop: 8 }}>
        <Text strong style={{ fontSize: 12, color: "#5e6c84" }}>
          Preguntas de esta sección
        </Text>
        {esPuntos ? (
          <Tag color="blue">Puntos posibles en esta sección: {totalPuntosSeccion}</Tag>
        ) : (
          tienePonderables && (
            <Tag color={Math.round(sumaPreguntas) > 0 ? "blue" : "red"}>
              Suma de pesos de las preguntas (relativa): {Math.round(sumaPreguntas)}
            </Tag>
          )
        )}
      </div>
      {!esPuntos && tienePonderables && (
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4, marginBottom: 8 }}>
          Los pesos de las preguntas de esta sección son relativos entre ellas — no necesitan
          sumar 100 (las de tipo Texto, o marcadas como "no cuenta para el resultado", no
          entran en esta suma).
        </Paragraph>
      )}

      <Form.List name={[name, "preguntas"]}>
        {(pFields, { add: addPregunta, remove: removePregunta }) => (
          <>
            {pFields.map((pField, pIndex) => {
              const preguntaValores = seccionValores?.preguntas?.[pField.name];
              return (
                <DynamicCardForm
                  key={pField.key}
                  title={<ResumenPregunta index={pIndex} pregunta={preguntaValores} esPuntos={esPuntos} />}
                  collapsable
                  defaultOpen
                  compact
                  noMargin
                  style={{ marginBottom: 8 }}
                  headerRight={
                    !disabled && (
                      <ButtonItem
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        tooltip="Eliminar esta pregunta"
                        onClick={() => removePregunta(pField.name)}
                      />
                    )
                  }
                >
                  <PreguntaRow
                    pField={pField}
                    disabled={disabled}
                    esPuntos={esPuntos}
                    preguntaValores={preguntaValores}
                    preguntaAnterior={seccionValores?.preguntas?.[pField.name - 1]}
                    onCopiarOpciones={() => onCopiarOpciones(pField.name)}
                    escalaGeneral={escalaGeneral}
                    onUsarEscalaGeneral={() => onUsarEscalaGeneral(pField.name)}
                    numeroGeneral={numeroGeneral}
                    onUsarNumeroGeneral={() => onUsarNumeroGeneral(pField.name)}
                    booleanoGeneral={booleanoGeneral}
                    onUsarBooleanoGeneral={() => onUsarBooleanoGeneral(pField.name)}
                  />
                </DynamicCardForm>
              );
            })}

            {!disabled && (
              <ButtonItem
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addPregunta(preguntaVacia(escalaGeneral))}
                style={{ marginTop: 4 }}
              >
                Añadir Pregunta
              </ButtonItem>
            )}
          </>
        )}
      </Form.List>
    </Card>
  );
}

function ResumenPregunta({ index, pregunta, esPuntos }) {
  return (
    <Space wrap style={{ textTransform: "none", letterSpacing: "normal", fontWeight: 400 }}>
      <Text strong>{index + 1}.</Text>
      <Text>{pregunta?.descripcion || "Nueva pregunta (sin descripción)"}</Text>
      <Tag color="blue">{TIPO_ABREV[pregunta?.tipo] || "…"}</Tag>
      {!esPuntos && pregunta?.tipo !== "texto" && pregunta?.cuentaParaResultado !== false && (
        <Tag>peso {pregunta?.peso ?? 0}</Tag>
      )}
      {pregunta?.obligatoria && <Tag color="red">obligatoria</Tag>}
      {pregunta?.cuentaParaResultado === false && <Tag>no cuenta</Tag>}
    </Space>
  );
}

function PreguntaRow({
  pField,
  disabled,
  preguntaValores,
  esPuntos,
  preguntaAnterior,
  onCopiarOpciones,
  escalaGeneral,
  onUsarEscalaGeneral,
  numeroGeneral,
  onUsarNumeroGeneral,
  booleanoGeneral,
  onUsarBooleanoGeneral,
}) {
  const tipo = preguntaValores?.tipo;
  const cuenta = preguntaValores?.cuentaParaResultado !== false;
  const puedeCopiarOpciones =
    tipo === "escala" && preguntaAnterior?.tipo === "escala" && (preguntaAnterior?.opciones || []).length > 0;
  const puedeUsarEscalaGeneral = tipo === "escala" && (escalaGeneral || []).length > 0;
  const puedeUsarNumeroGeneral =
    tipo === "num" &&
    (numeroGeneral?.valorMinimo != null ||
      numeroGeneral?.valorMaximo != null ||
      numeroGeneral?.etiquetaMinimo ||
      numeroGeneral?.etiquetaMaximo);
  const puedeUsarBooleanoGeneral = tipo === "bool" && booleanoGeneral?.valorSiCumple != null;

  return (
    <div
      style={{
        border: "1px solid #ebedf0",
        borderRadius: 6,
        padding: "12px 14px",
        marginBottom: 12,
        background: "#fff",
      }}
    >
      <Row gutter={[12, 12]}>
        <Col span={16}>
          <FormItem
            name={[pField.name, "descripcion"]}
            label="Pregunta"
            rules={[{ required: true, message: "Describe la pregunta" }]}
            {...FULL}
            tooltip="El texto que va a ver quien llene la evaluación. Ej. '¿Tiene precios competitivos para su servicio?'"
          >
            <InputItem placeholder="Ej. ¿Cumple con los tiempos de entrega?" disabled={disabled} />
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem
            name={[pField.name, "tipo"]}
            label="Tipo de Respuesta"
            rules={[{ required: true }]}
            {...FULL}
            tooltip="Escala: opciones con puntos (ej. Malo=1…Excelente=4). Numérico: un valor contra un máximo. Sí/No: interruptor. Texto: comentario libre que no puntúa."
          >
            <SelectItem disabled={disabled} placeholder="Elige el tipo">
              {TIPOS_PREGUNTA.map((t) => (
                <Select.Option key={t.value} value={t.value}>
                  {t.label}
                </Select.Option>
              ))}
            </SelectItem>
          </FormItem>
        </Col>
        <Col span={24}>
          <FormItem
            name={[pField.name, "textoAyuda"]}
            label="Texto de ayuda (leyenda debajo de la pregunta, opcional)"
            {...FULL}
            tooltip="Aclaración que verá quien llena la evaluación justo debajo de la pregunta, en letra chica. Ej. '(profesionalismo y calidad de la atención en general)'."
          >
            <InputItem placeholder="Ej. Rapidez y precisión en los tiempos de llegada y salida" disabled={disabled} />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={[12, 12]} align="bottom">
        {!esPuntos && (
          <Col span={6}>
            <FormItem
              name={[pField.name, "peso"]}
              label="Peso en la Sección"
              {...FULL}
              tooltip="Peso RELATIVO de esta pregunta frente a las demás de su sección (no necesita sumar 100). No aplica si el tipo es Texto o si 'Cuenta para el resultado' está apagado."
            >
              <InputNumberItem
                min={0}
                style={{ width: "100%" }}
                disabled={disabled || tipo === "texto" || !cuenta}
                placeholder={tipo === "texto" ? "No aplica" : "Peso"}
              />
            </FormItem>
          </Col>
        )}
        <Col span={esPuntos ? 8 : 6}>
          <FormItem
            name={[pField.name, "permiteNoAplica"]}
            label="Permite 'No aplica'"
            valuePropName="checked"
            {...FULL}
            tooltip="Si se activa, quien llena la evaluación puede marcar esta pregunta como 'No aplica'. Esa pregunta sale del cálculo y el resto se re-normaliza, en vez de contar como 0."
          >
            <SwitchItem disabled={disabled} />
          </FormItem>
        </Col>
        <Col span={esPuntos ? 8 : 6}>
          <FormItem
            name={[pField.name, "obligatoria"]}
            label="Obligatoria"
            valuePropName="checked"
            {...FULL}
            tooltip="Si se activa, esta pregunta SÍ bloquea: no se va a poder completar la evaluación mientras no se responda (o se marque 'No aplica')."
          >
            <SwitchItem disabled={disabled} />
          </FormItem>
        </Col>
        <Col span={esPuntos ? 8 : 6}>
          <FormItem
            name={[pField.name, "cuentaParaResultado"]}
            label="Cuenta para el resultado"
            valuePropName="checked"
            {...FULL}
            tooltip="Apágalo si quieres capturar la respuesta pero que NO afecte la calificación — se guarda igual, pero se trata como si fuera de tipo Texto para el cálculo."
          >
            <SwitchItem disabled={disabled || tipo === "texto"} />
          </FormItem>
        </Col>
      </Row>

      {tipo === "escala" && (
        <>
          <Divider style={{ margin: "8px 0" }} orientation="left" plain>
            <Text style={{ fontSize: 12 }}>Opciones de la escala</Text>
          </Divider>
          {!disabled && (puedeCopiarOpciones || puedeUsarEscalaGeneral) && (
            <Space style={{ marginBottom: 8 }} wrap>
              {puedeCopiarOpciones && (
                <ButtonItem size="small" icon={<CopyOutlined />} onClick={onCopiarOpciones}>
                  Copiar opciones de la pregunta anterior
                </ButtonItem>
              )}
              {puedeUsarEscalaGeneral && (
                <ButtonItem size="small" icon={<CopyOutlined />} onClick={onUsarEscalaGeneral}>
                  Usar Escala General de la plantilla
                </ButtonItem>
              )}
            </Space>
          )}
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4 }}>
            {esPuntos ? (
              <>
                Cada opción tiene una etiqueta visible (ej. "Excelente") y un valor en{" "}
                <b>puntos</b> — ese valor se suma tal cual al total, no se convierte a
                porcentaje.
              </>
            ) : (
              <>
                Cada opción tiene una etiqueta visible (ej. "Excelente") y un valor numérico. La
                opción con el valor más alto se toma como el 100%; las demás se calculan
                proporcionalmente. Ej. si tus opciones van de 0 a 4 (como Malo=1…Excelente=4),
                responder "2" equivale a 2/4 = 50%.
              </>
            )}
          </Paragraph>
          <Form.List name={[pField.name, "opciones"]}>
            {(oFields, { add: addOpcion, remove: removeOpcion }) => (
              <div>
                {oFields.map((oField) => (
                  <Row gutter={[8, 8]} key={oField.key} align="middle">
                    <Col span={11}>
                      <FormItem
                        name={[oField.name, "etiqueta"]}
                        {...SIN_LABEL}
                        tooltip="Texto visible de la opción, ej. 'Cumple' o 'Excelente'."
                      >
                        <InputItem placeholder="Etiqueta (ej. Excelente)" disabled={disabled} />
                      </FormItem>
                    </Col>
                    <Col span={9}>
                      <FormItem
                        name={[oField.name, "valor"]}
                        {...SIN_LABEL}
                        tooltip={
                          esPuntos
                            ? "Puntos que otorga elegir esta opción — se suman tal cual al total, no se convierten a porcentaje."
                            : "Puntos que otorga elegir esta opción. La opción con más puntos = 100%."
                        }
                      >
                        <InputNumberItem placeholder="Valor / puntos" style={{ width: "100%" }} disabled={disabled} />
                      </FormItem>
                    </Col>
                    <Col span={4}>
                      {!disabled && (
                        <FormItem {...SIN_LABEL}>
                          <ButtonItem
                            danger
                            size="small"
                            icon={<MinusCircleOutlined />}
                            onClick={() => removeOpcion(oField.name)}
                            tooltip="Eliminar esta opción"
                          />
                        </FormItem>
                      )}
                    </Col>
                  </Row>
                ))}
                {!disabled && (
                  <ButtonItem
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addOpcion({ etiqueta: "", valor: 0 })}
                    style={{ marginTop: 4 }}
                  >
                    Agregar Opción
                  </ButtonItem>
                )}
              </div>
            )}
          </Form.List>
        </>
      )}

      {tipo === "num" && (
        <>
          <Divider style={{ margin: "8px 0" }} orientation="left" plain>
            <Text style={{ fontSize: 12 }}>Rango numérico</Text>
          </Divider>
          {puedeUsarNumeroGeneral && !disabled && (
            <ButtonItem
              size="small"
              icon={<CopyOutlined />}
              onClick={onUsarNumeroGeneral}
              style={{ marginBottom: 8 }}
            >
              Usar configuración general
            </ButtonItem>
          )}
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4 }}>
            {esPuntos ? (
              <>
                Quien llena la evaluación captura los <b>puntos ya resueltos</b> (no un
                porcentaje). Si tu papel usa una tabla de rangos con puntos fijos (ej. "0
                retrasos = 150 pts, 1 retraso = 130 pts…"), aquí se captura directamente el
                resultado de esa tabla — ej. si tuvo 1 retraso, se escribe "130", no "1".
              </>
            ) : (
              <>
                Quien llena la evaluación captura un número; se compara contra el máximo para
                sacar el porcentaje. Ej. si el máximo es 10 y captura 8, equivale a 80%.
              </>
            )}
          </Paragraph>
          <Row gutter={[12, 12]}>
            <Col span={6}>
              <FormItem
                name={[pField.name, "valorMinimo"]}
                label="Valor Mínimo (opcional)"
                {...FULL}
                tooltip="Solo informativo/referencia, ej. 0."
              >
                <InputNumberItem style={{ width: "100%" }} disabled={disabled} />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem
                name={[pField.name, "etiquetaMinimo"]}
                label="Nombre del Mínimo (opcional)"
                {...FULL}
                tooltip="Cómo se le llama a ese extremo, ej. 'Malo' o '0 retrasos'. Solo informativo, no afecta el cálculo."
              >
                <InputItem placeholder="Ej. Malo" disabled={disabled} />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem
                name={[pField.name, "valorMaximo"]}
                label={esPuntos ? "Valor Máximo (tope de puntos)" : "Valor Máximo (100%)"}
                rules={[{ required: true, message: "Requerido" }]}
                {...FULL}
                tooltip={
                  esPuntos
                    ? "El máximo de puntos que puede valer esta pregunta, ej. 150 o 400."
                    : "El valor que representa el 100% de esta pregunta. Ej. si la meta es 10 entregas a tiempo, pon 10."
                }
              >
                <InputNumberItem style={{ width: "100%" }} disabled={disabled} />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem
                name={[pField.name, "etiquetaMaximo"]}
                label="Nombre del Máximo (opcional)"
                {...FULL}
                tooltip="Cómo se le llama a ese extremo, ej. 'Excelente' o '10 entregas a tiempo'. Solo informativo, no afecta el cálculo."
              >
                <InputItem placeholder="Ej. Excelente" disabled={disabled} />
              </FormItem>
            </Col>
          </Row>
        </>
      )}

      {tipo === "bool" && (
        <>
          <Divider style={{ margin: "8px 0" }} orientation="left" plain>
            <Text style={{ fontSize: 12 }}>Configuración Sí / No</Text>
          </Divider>
          {puedeUsarBooleanoGeneral && !disabled && (
            <ButtonItem
              size="small"
              icon={<CopyOutlined />}
              onClick={onUsarBooleanoGeneral}
              style={{ marginBottom: 8 }}
            >
              Usar configuración general
            </ButtonItem>
          )}
          <Row gutter={[12, 12]}>
            <Col span={10}>
              <FormItem
                name={[pField.name, "valorSiCumple"]}
                label={esPuntos ? "Puntos si la respuesta es 'Sí'" : "Puntos si la respuesta es 'Sí' (0-100)"}
                {...FULL}
                tooltip="Si responden 'Sí' se otorgan estos puntos. Si responden 'No', siempre se otorgan 0 puntos."
              >
                <InputNumberItem min={0} max={esPuntos ? undefined : 100} style={{ width: "100%" }} disabled={disabled} />
              </FormItem>
            </Col>
          </Row>
        </>
      )}

      {tipo === "texto" && (
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Esta pregunta no puntúa: solo queda guardada como comentario libre en la evaluación.
        </Paragraph>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  permisoAutorizarPlantilla:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (v) => v.permisoId.toString() === "6a726d4a84bfe1e26a3e4c6b",
    )?.activado,
});

export default connect(mapStateToProps)(VistaPlantillaEvaluacion);
